import classNames from 'classnames'
import { FC, useContext } from 'react'
import { useTranslation } from 'react-i18next'

import { useAccountReadContext } from '@context/AccountContextContext'
import { AuthSessionContext } from '@context/AuthContext'
import { useArabicSessionLanguage } from '@context/SessionSettingsContext'

import { SensitiveData } from '@ui/Privacy/SensitiveData'
import { Body1, Body2 } from '@ui/Typography'
import { Badge } from '@ui/components/Badge/Badge'
import { Tooltip } from '@ui/components/Tooltip/Tooltip'

import { useGAEvents } from '@utils/callGAEvents'
import { formatDate } from '@utils/date.utils'
import {
  getIBTransactionFieldsByTransactionType,
  isTransactionPositive,
} from '@utils/transaction.utils'
import useSensitiveDataFlag from '@utils/useSensitiveDataFlag'

import { HorizontalScroll } from '@global/HorizontalScroll/HorizontalScroll'

import { useFormatNumber } from '@hooks/useFormatNumber'

import { Box } from '@shared/components/Box'
import { Loading } from '@shared/components/Loading/Loading'
import { TransactionBadgeMap } from '@shared/components/StatusBadge/StatusBadge'
import { TransactionTypeIcon } from '@shared/components/TransactionTypeIcon/TransactionTypeIcon'

import { EmptyTransactionHistory } from '../EmptyTransactionHistory/EmptyTransactionHistory'
import { SectionHeader } from '../components/SectionHeader/SectionHeader'
import { WalletWithLatestTransactions } from '../hooks/useWalletsWithLatestTransactions'

import styles from './TransactionHistory.module.scss'

interface IBTransactionHistoryProps {
  walletsWithLatestTransactions: WalletWithLatestTransactions[]
  isLoading: boolean
  className?: string
}

export const TransactionHistory: FC<IBTransactionHistoryProps> = (props) => {
  const { walletsWithLatestTransactions, isLoading, className } = props
  const { account } = useAccountReadContext()

  const { t } = useTranslation()
  const isArabic = useArabicSessionLanguage()
  const { formatMoney } = useFormatNumber()
  const [auth] = useContext(AuthSessionContext)
  const { callGAEvents } = useGAEvents()
  const sensitiveDataFlag = useSensitiveDataFlag()

  const dateFormat = auth?.dateFormatType?.name
  const hasAnyTransactions = walletsWithLatestTransactions.some(
    (walletWithLatestTransaction) => walletWithLatestTransaction.latestTransactions.length
  )
  const showWalletName = walletsWithLatestTransactions.length > 1
  return (
    <div className={classNames(styles.ibTransactionHistory, className)}>
      <SectionHeader
        title={t('IB.Dashboard.Transaction history')}
        seeMoreLinkProps={{
          onClick: () => callGAEvents().IBDashboard.TransactionHistory.seeMore(),
          to: '/dashboard/introducing-broker/reports/transactions-history',
        }}
      />
      <Loading isLoading={isLoading} showLoadingIcon>
        {walletsWithLatestTransactions.length && hasAnyTransactions ? (
          <HorizontalScroll
            containerClassName={styles.transactionsContainer}
            innerClassName={styles.transactionsContainerScroll}
          >
            {walletsWithLatestTransactions.slice(0, 2).map((walletWithLatestTransaction) => {
              const { wallet, latestTransactions } = walletWithLatestTransaction

              if (!latestTransactions.length) {
                return null
              }

              return (
                <div className={styles.transactionsWrapper} key={wallet.id}>
                  {latestTransactions.slice(0, 3).map((transaction) => {
                    const fields = transaction
                      ? getIBTransactionFieldsByTransactionType(
                          transaction.type.id,
                          transaction,
                          isArabic,
                          account?.id
                        )
                      : undefined

                    const transactionBadge = TransactionBadgeMap[transaction.state.id]
                    const isPositive = isTransactionPositive(transaction, account?.id)

                    return (
                      <div className={styles.transaction} key={transaction.id}>
                        <TransactionTypeIcon transaction={transaction} />
                        <div className={styles.transactionDetails}>
                          <div className={styles.transactionDetailsCol}>
                            <Box align='center' flex gap='x8'>
                              <Tooltip
                                content={transaction.type.name}
                                wrapperClassName={styles.tooltipWrapper}
                              >
                                <Body2 className={styles.transactionName} medium>
                                  {transaction.type.name}
                                </Body2>
                              </Tooltip>
                              {showWalletName && (
                                <Badge size='xs' contrast='low'>
                                  {wallet.name}
                                </Badge>
                              )}
                            </Box>
                            <Body2>
                              <SensitiveData>
                                {formatDate(transaction.createdDate, { formatType: dateFormat })}
                              </SensitiveData>
                            </Body2>
                          </div>
                          <div className={styles.transactionDetailsCol}>
                            {fields && (
                              <Body1
                                className={classNames(isPositive && styles.amountGreen)}
                                medium
                              >
                                <SensitiveData>
                                  <span>{isPositive ? '+' : '-'}</span>
                                  {formatMoney(fields.amount, fields.currency)}
                                </SensitiveData>
                              </Body1>
                            )}

                            {!sensitiveDataFlag ? (
                              <Tooltip
                                content={t(transactionBadge.label)}
                                wrapperClassName={classNames(
                                  styles.tooltipWrapper,
                                  styles.alignEnd
                                )}
                              >
                                <Body2 className={classNames(styles.transactionName)}>
                                  {t(transactionBadge.label)}
                                </Body2>
                              </Tooltip>
                            ) : (
                              <SensitiveData>
                                <Body2 className={classNames(styles.transactionName)}>
                                  {t(transactionBadge.label)}
                                </Body2>
                              </SensitiveData>
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )
            })}
          </HorizontalScroll>
        ) : (
          <EmptyTransactionHistory />
        )}
      </Loading>
    </div>
  )
}
